import { useInfiniteQuery } from '@tanstack/react-query'
import { Empty, Flex, Select, Spin } from 'antd'
import { SelectProps } from 'antd/lib'
import { useMemo, useState } from 'react'

import { useClassifierServiceGetCuatmClassifierElementListEndpointKey } from 'src/api/queries'
import {
  ClassifierCategory,
  ClassifierService,
  ClassifierType,
  GetCuatmClassifierElementListResponseItem
} from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import { useClassifierId, useDebounce } from 'src/hooks'
import { useLocales } from 'src/locales'
import { getLocalizedTitle } from 'src/utils'

export const CUATMInfiniteSelect = (props: SelectProps) => {
  const { currentLang, t } = useLocales()
  const [search, setSearch] = useState('')
  const debounceSearch = useDebounce(search)

  const cuatmClassifierId = useClassifierId(
    ClassifierType.CUATM,
    ClassifierCategory.DIAGNOSTIC
  )
  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: [
      useClassifierServiceGetCuatmClassifierElementListEndpointKey,
      String(cuatmClassifierId),
      debounceSearch
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response =
        await ClassifierService.getCuatmClassifierElementListEndpoint(
          pageParam,
          DEFAULT_PER_PAGE,
          undefined,
          debounceSearch,
          debounceSearch
        )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data: GetCuatmClassifierElementListResponseItem[] | undefined
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1,
    enabled: !!cuatmClassifierId
  })

  const selectOptions = useMemo(
    () =>
      data?.pages.flatMap(
        (page) =>
          page.data?.map((item: GetCuatmClassifierElementListResponseItem) => ({
            value: item?.id,
            label: getLocalizedTitle({
              item,
              t,
              currentLang: currentLang.value
            })
          })) || []
      ) || [],
    [data, currentLang]
  )

  return (
    <Select
      allowClear
      filterOption={false}
      onSearch={(value) => setSearch(value)}
      onClear={() => setSearch('')}
      options={selectOptions}
      loading={isFetching}
      placeholder={t('organizational_structure.city')}
      showSearch
      onPopupScroll={(e) => {
        const target = e.target as HTMLElement
        if (target.scrollTop + target.offsetHeight === target.scrollHeight)
          fetchNextPage()
      }}
      notFoundContent={
        isFetching ? (
          <Flex justify="center">
            <Spin spinning />
          </Flex>
        ) : (
          <Empty />
        )
      }
      {...props}
    />
  )
}
